import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import paymentGatewaysService from '../services/paymentGatewaysService';

const STATUS_BADGE = ({ enabled }) =>
  enabled ? (
    <span className="px-2 py-0.5 bg-green-500/10 text-green-600 text-[10px] font-bold rounded-full uppercase">Connected</span>
  ) : (
    <span className="px-2 py-0.5 bg-muted text-muted-foreground text-[10px] font-bold rounded-full uppercase">Disconnected</span>
  );

const Toggle = ({ enabled, disabled, onToggle }) => (
  <label className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
    <input type="checkbox" className="sr-only peer" checked={enabled} onChange={onToggle} disabled={disabled} />
    <div className="w-11 h-6 bg-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
  </label>
);

const GATEWAY_META = {
  paystack: {
    name: 'Paystack',
    icon: 'simple-icons:paystack',
    bg: 'bg-[#00C3F7]/10',
    color: 'text-[#00C3F7]',
    description: 'Accept payments from Nigerian and African customers via card, bank transfer, and USSD.',
  },
  moments: {
    name: 'Moments',
    icon: 'lucide:zap',
    bg: 'bg-orange-500/10',
    color: 'text-orange-500',
    description: 'Accept payments via the Moments payment platform.',
  },
};

function GatewayModal({ gateway, config, onClose, onSuccess }) {
  const meta = GATEWAY_META[gateway];
  const isConnected = !!(config?.publicKey);
  const [publicKey, setPublicKey] = useState(config?.publicKey || '');
  const [secretKey, setSecretKey] = useState('');
  const [mode, setMode] = useState(config?.mode || 'test');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!publicKey.trim()) { setError('Public key is required'); return; }
    if (!isConnected && !secretKey.trim()) { setError('Secret key is required'); return; }
    setLoading(true);
    setError('');
    try {
      if (isConnected) {
        await paymentGatewaysService.configure(gateway, { publicKey, secretKey: secretKey || '****', mode });
      } else {
        await paymentGatewaysService.connect(gateway, { publicKey, secretKey, mode });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save gateway configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-2xl border border-border shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${meta.bg} rounded-xl flex items-center justify-center`}>
              <Icon icon={meta.icon} className={`text-2xl ${meta.color}`} />
            </div>
            <div>
              <h3 className="text-sm font-bold">{isConnected ? 'Configure' : 'Connect'} {meta.name}</h3>
              <p className="text-xs text-muted-foreground">Mode: {mode}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors">
            <Icon icon="lucide:x" className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <p className="text-sm text-destructive bg-destructive/10 px-4 py-2 rounded-xl">{error}</p>}

          <div className="space-y-2">
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Public Key</label>
            <input
              type="text"
              value={publicKey}
              onChange={e => setPublicKey(e.target.value)}
              placeholder={gateway === 'paystack' ? 'pk_test_...' : 'Your public key'}
              className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none font-mono"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Secret Key {isConnected && <span className="text-muted-foreground">(leave blank to keep current)</span>}
            </label>
            <input
              type="password"
              value={secretKey}
              onChange={e => setSecretKey(e.target.value)}
              placeholder={isConnected ? '****' : (gateway === 'paystack' ? 'sk_test_...' : 'Your secret key')}
              className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none font-mono"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Mode</label>
            <select
              value={mode}
              onChange={e => setMode(e.target.value)}
              className="w-full px-4 py-2.5 bg-muted border-none rounded-xl text-sm focus:ring-2 focus:ring-primary outline-none"
            >
              <option value="test">Test</option>
              <option value="live">Live</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-border text-muted-foreground rounded-xl text-xs font-bold hover:bg-muted transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50">
              {loading ? 'Saving…' : (isConnected ? 'Save Changes' : 'Connect')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function GatewayCard({ gateway, config, onRefresh }) {
  const meta = GATEWAY_META[gateway];
  const isConnected = !!(config?.publicKey);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleToggle = async () => {
    if (!isConnected) return;
    setToggling(true);
    try {
      await paymentGatewaysService.toggle(gateway);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle gateway');
    } finally {
      setToggling(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await paymentGatewaysService.disconnect(gateway);
      setShowConfirm(false);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to disconnect gateway');
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <>
      {showModal && (
        <GatewayModal
          gateway={gateway}
          config={config}
          onClose={() => setShowModal(false)}
          onSuccess={onRefresh}
        />
      )}

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-2xl border border-border shadow-xl w-full max-w-sm p-6">
            <h3 className="text-sm font-bold mb-2">Disconnect {meta.name}?</h3>
            <p className="text-sm text-muted-foreground mb-6">This will remove all saved keys and disable the gateway. Customers will no longer be able to pay via {meta.name}.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowConfirm(false)} className="px-4 py-2 border border-border text-muted-foreground rounded-xl text-xs font-bold hover:bg-muted transition-colors">Cancel</button>
              <button onClick={handleDisconnect} disabled={disconnecting} className="px-4 py-2 bg-destructive text-white rounded-xl text-xs font-bold hover:opacity-90 transition-opacity disabled:opacity-50">
                {disconnecting ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-5 bg-muted/30 rounded-xl border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 ${meta.bg} rounded-xl flex items-center justify-center`}>
            <Icon icon={meta.icon} className={`text-2xl ${meta.color}`} />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-bold">{meta.name}</p>
              <STATUS_BADGE enabled={isConnected && config?.enabled} />
              {isConnected && config?.mode && (
                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${config.mode === 'live' ? 'bg-green-500/10 text-green-600' : 'bg-yellow-500/10 text-yellow-600'}`}>
                  {config.mode}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">{meta.description}</p>
            {isConnected && config?.publicKey && (
              <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                {config.publicKey.slice(0, 12)}…
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Toggle enabled={isConnected && config?.enabled} disabled={!isConnected || toggling} onToggle={handleToggle} />

          {isConnected ? (
            <>
              <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-card border border-border rounded-xl text-xs font-bold hover:bg-muted transition-colors">
                Configure
              </button>
              <button onClick={() => setShowConfirm(true)} className="px-4 py-2 bg-destructive/10 text-destructive rounded-xl text-xs font-bold hover:bg-destructive hover:text-white transition-all">
                Disconnect
              </button>
            </>
          ) : (
            <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-xs font-bold hover:opacity-90 transition-opacity">
              Connect
            </button>
          )}
        </div>
      </div>
    </>
  );
}

export default function PaymentGateways() {
  const [gateways, setGateways] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchGateways = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await paymentGatewaysService.getGateways();
      setGateways(data);
    } catch (err) {
      setError('Failed to load payment gateways');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGateways(); }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        {['paystack', 'moments'].map(gw => (
          <div key={gw} className="p-5 bg-muted/30 rounded-xl border border-border animate-pulse h-24" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-4 bg-destructive/10 text-destructive rounded-xl text-sm">{error}</div>
      )}

      <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
        <h2 className="text-lg font-heading font-bold mb-6">Payment Gateways</h2>
        <div className="space-y-4">
          <GatewayCard gateway="paystack" config={gateways.paystack} onRefresh={fetchGateways} />
          <GatewayCard gateway="moments" config={gateways.moments} onRefresh={fetchGateways} />
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border shadow-sm p-6">
        <h2 className="text-lg font-heading font-bold mb-4">How It Works</h2>
        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="flex gap-3">
            <Icon icon="lucide:info" className="text-primary shrink-0 mt-0.5" />
            <p>Connect at least one gateway to accept payments on the customer site.</p>
          </div>
          <div className="flex gap-3">
            <Icon icon="lucide:shield" className="text-primary shrink-0 mt-0.5" />
            <p>Secret keys are encrypted at rest using AES-256. They are never exposed to the frontend.</p>
          </div>
          <div className="flex gap-3">
            <Icon icon="lucide:toggle-right" className="text-primary shrink-0 mt-0.5" />
            <p>Use the toggle to enable or disable a gateway without removing your keys.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
